"use client";

import { Tab, Nav, Row, Col, Card } from "react-bootstrap";
import { useState, useEffect } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import styles from "./info-tabs.module.scss";
import BackgroundTab from "./BackgroundTab";
import EcosystemServicesTab from "./EcosystemServicesTab";
import LayersTab from "./LayersTab";
import EcotopeAnalysisTab from "./EcotopeAnalysisTab";
import SlopeAnalysisTab from "./SlopeAnalysis";
import TimeSeriesTab from "./TimeSeriesTab";
import EcotopeTypesTab from "./EcotopeTypesTab";
import ReferencesTab from "./ReferencesTab";

import ArticleIcon from '@mui/icons-material/Article';
import NatureIcon from '@mui/icons-material/Nature';
import MapIcon from '@mui/icons-material/Map';
import TimelineIcon from '@mui/icons-material/Timeline';
import GrassIcon from '@mui/icons-material/Grass';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import MenuBookIcon from '@mui/icons-material/MenuBook';

const InfoTabs = () => {
  const [activeKey, setActiveKey] = useState<string>("background");
  const [isTransitioning, setIsTransitioning] = useState(false);

  const searchParams = useSearchParams();
  const pathname = usePathname();

  const handleLinkClick = (e: React.MouseEvent, id: string) => {
    e.preventDefault();
    setIsTransitioning(true);
    setActiveKey("references");

    setTimeout(() => {
      document.getElementById(id)?.scrollIntoView({
        behavior: "smooth",
        block: "start"
      });
      setIsTransitioning(false);
    }, 300);
  };

  useEffect(() => {
    const qp = searchParams.get("section");
    const hash = pathname.split("#")[1];
    const wanted = qp || hash;
    if (wanted) setActiveKey(wanted);
  }, [searchParams, pathname]);

  return (
    <div className={styles.pageWrapper}>
      <main className={styles.container}>
        <Card className={styles.pageHeaderCard}>
          <Card.Body>
            <h1 className={styles.pageHeaderTitle}>
              Knowledge Hub
            </h1>
          </Card.Body>
        </Card>

        <Card className={styles.contentCard}>
          <Card.Body className="p-0">
            <Tab.Container
              id="peatland-tabs"
              activeKey={activeKey}
              onSelect={(k) => k && setActiveKey(k)}
              transition={false}
            >
              <Row className="g-0">
                <Col lg={3} className={styles.rowBorder}>
                  <Nav variant="pills" className={styles.tabListNav} style={{ position: "sticky", top: "20px" }}>
                    <Nav.Item>
                      <Nav.Link eventKey="background">
                        <span className={styles.iconWrapper}>
                          <ArticleIcon fontSize="small" />
                        </span>
                        Background
                      </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link eventKey="ecosystem">
                        <span className={styles.iconWrapper}>
                          <NatureIcon fontSize="small" />
                        </span>
                        Ecosystem Services
                      </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link eventKey="layers">
                        <span className={styles.iconWrapper}>
                          <MapIcon fontSize="small" />
                        </span>
                        Map Layers
                      </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link eventKey="ecotope">
                        <span className={styles.iconWrapper}>
                          <TimelineIcon fontSize="small" />
                        </span>
                        Ecological Zones Analysis
                      </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link eventKey="ecotope-types">
                        <span className={styles.iconWrapper}>
                          <GrassIcon fontSize="small" />
                        </span>
                        Ecotope Types
                      </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link eventKey="slope-analysis">
                        <span className={styles.iconWrapper}>
                          <ShowChartIcon fontSize="small" />
                        </span>
                        Slope Analysis
                      </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link eventKey="timeseries-analysis">
                        <span className={styles.iconWrapper}>
                          <WaterDropIcon fontSize="small" />
                        </span>
                        Volumetric Water Balance
                      </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link eventKey="references">
                        <span className={styles.iconWrapper}>
                          <MenuBookIcon fontSize="small" />
                        </span>
                        References
                      </Nav.Link>
                    </Nav.Item>
                  </Nav>
                </Col>

                <Col lg={9}>
                  <div className={styles.tabContent}>
                    <Tab.Content>
                      <Tab.Pane eventKey="background">
                        <BackgroundTab handleLinkClick={handleLinkClick} />
                      </Tab.Pane>
                      <Tab.Pane eventKey="ecosystem">
                        <EcosystemServicesTab />
                      </Tab.Pane>
                      <Tab.Pane eventKey="layers">
                        <LayersTab />
                      </Tab.Pane>
                      <Tab.Pane eventKey="ecotope">
                        <EcotopeAnalysisTab handleLinkClick={handleLinkClick} />
                      </Tab.Pane>
                      <Tab.Pane eventKey="slope-analysis">
                        <SlopeAnalysisTab />
                      </Tab.Pane>
                      <Tab.Pane eventKey="timeseries-analysis">
                        <TimeSeriesTab />
                      </Tab.Pane>
                      <Tab.Pane eventKey="ecotope-types">
                        <EcotopeTypesTab />
                      </Tab.Pane>
                      <Tab.Pane eventKey="references">
                        <ReferencesTab />
                      </Tab.Pane>
                    </Tab.Content>
                  </div>
                </Col>
              </Row>
            </Tab.Container>
          </Card.Body>
        </Card>
      </main>
    </div>
  );
};

export default InfoTabs;